import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchOrderByGuId,
  fetchOrderShippingAddressByGuid,
  fetchOrderBillingAddressByGuid,
  fetchOrderItemsByGuId,
  cancelOrder,
  confirmOrder,
  dispatchOrder,
  deliverOrder,
  markOrderPaid,
  fetchOrderPaymentsByGuId,
} from "../../../services/orderService";
import { Loading } from "../../Common/OtherElements/Loading";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import Swal from "sweetalert2";

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orderPaymentItems, setOrderPaymentItems] = useState([]);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [billingAddress, setBillingAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [pageAccessDetails, setPageAccessDetails] = useState([]);

  const PageLevelAccessurl = "order";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  useEffect(() => {
    if (id && pageAccessDetails.viewAccess) {
      fetchOrderDetails();
    }
  }, [id, pageAccessDetails]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);

      const [orderData, itemsData, shippingData, billingData, paymentsData] =
        await Promise.allSettled([
          fetchOrderByGuId(id),
          fetchOrderItemsByGuId(id),
          fetchOrderShippingAddressByGuid(id),
          fetchOrderBillingAddressByGuid(id),
          fetchOrderPaymentsByGuId(id),
        ]);

      if (orderData.status === "fulfilled" && orderData.value?.isSuccess) {
        setOrder(orderData.value.result || orderData.value);
      }
      if (itemsData.status === "fulfilled" && itemsData.value?.isSuccess) {
        setOrderItems(itemsData.value.result || itemsData.value);
      }
      if (
        shippingData.status === "fulfilled" &&
        shippingData.value?.isSuccess
      ) {
        setShippingAddress(shippingData.value.result || shippingData.value);
      }
      if (billingData.status === "fulfilled" && billingData.value?.isSuccess) {
        setBillingAddress(billingData.value.result || billingData.value);
      }
      if (
        paymentsData.status === "fulfilled" &&
        paymentsData.value?.isSuccess
      ) {
        setOrderPaymentItems(paymentsData.value.result || []);
      }
    } catch (error) {
      console.error("Failed to fetch order details:", error);
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, action, newStatus) => {
    const result = await Swal.fire({
      title: `${action} Order?`,
      text: `Are you sure you want to ${action.toLowerCase()} this order?`,
      icon: action.toLowerCase() === "cancel" ? "warning" : "question",
      showCancelButton: true,
      confirmButtonColor:
        action.toLowerCase() === "cancel" ? "#d33" : "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Yes, ${action}!`,
    });

    if (result.isConfirmed) {
      setUpdatingOrderId(orderId);
      try {
        let response;

        switch (action.toLowerCase()) {
          case "confirm":
            response = await confirmOrder(orderId);
            break;
          case "dispatch":
            response = await dispatchOrder(orderId);
            break;
          case "deliver":
            response = await deliverOrder(orderId);
            break;
          case "cancel":
            response = await cancelOrder(orderId);
            break;
          default:
            throw new Error("Invalid action");
        }

        const isSuccess =
          response?.data?.isSuccess ||
          response?.isSuccess ||
          response?.status === 200;

        if (isSuccess) {
          setOrder((prev) => ({ ...prev, orderStatus: newStatus }));

          const successMessage =
            response?.data?.statusMessage ||
            response?.statusMessage ||
            `Order ${action.toLowerCase()}ed successfully!`;

          Swal.fire("Success!", successMessage, "success");
        } else {
          throw new Error(
            response?.data?.statusMessage ||
              response?.statusMessage ||
              `Failed to ${action.toLowerCase()} order`
          );
        }
      } catch (error) {
        console.error(`Error ${action.toLowerCase()}ing order:`, error);
        let errorMessage = `Failed to ${action.toLowerCase()} order`;
        if (error.response?.data?.statusMessage) {
          errorMessage = error.response.data.statusMessage;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        Swal.fire("Error!", errorMessage, "error");
      } finally {
        setUpdatingOrderId(null);
      }
    }
  };

  const handlePaymentStatusUpdate = async (orderId) => {
    const result = await Swal.fire({
      title: "Mark Order as Paid?",
      text: "Are you sure you want to mark this order as paid?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Mark as Paid!",
    });

    if (result.isConfirmed) {
      setUpdatingOrderId(orderId);
      try {
        const response = await markOrderPaid(orderId);
        const isSuccess =
          response?.data?.isSuccess ||
          response?.isSuccess ||
          response?.status === 200;

        if (isSuccess) {
          setOrder((prev) => ({
            ...prev,
            paymentStatus: "Paid",
            paidAmount: prev.totalPrice,
            balanceAmount: 0,
          }));

          const successMessage =
            response?.data?.statusMessage ||
            response?.statusMessage ||
            "Order marked as paid successfully!";

          Swal.fire("Success!", successMessage, "success");

          // Refresh payment details after updating
          fetchOrderDetails();
        } else {
          throw new Error(
            response?.data?.statusMessage ||
              response?.statusMessage ||
              "Failed to mark order as paid"
          );
        }
      } catch (error) {
        console.error("Error marking order as paid:", error);
        let errorMessage = "Failed to mark order as paid";
        if (error.response?.data?.statusMessage) {
          errorMessage = error.response.data.statusMessage;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        Swal.fire("Error!", errorMessage, "error");
      } finally {
        setUpdatingOrderId(null);
      }
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString("en-US", {
      hour12: true,
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${day}-${month}-${year} ${time}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "badge-soft-success";
      case "dispatched":
        return "badge-soft-info";
      case "confirmed":
        return "badge-soft-primary";
      case "in process":
        return "badge-soft-warning";
      case "cancelled":
        return "badge-soft-danger";
      case "paid":
        return "badge-soft-success";
      case "pending":
      case "initiated":
        return "badge-soft-warning";
      case "failed":
        return "badge-soft-danger";
      default:
        return "badge-soft-secondary";
    }
  };

  const getPaymentModeBadgeClass = (mode) => {
    switch (mode?.toLowerCase()) {
      case "stripe":
        return "badge-soft-primary";
      case "razorpay":
        return "badge-soft-info";
      case "paypal":
        return "badge-soft-warning";
      case "cash":
        return "badge-soft-success";
      default:
        return "badge-soft-secondary";
    }
  };

  const getAvailableActions = () => {
    if (!order) return [];

    const status = order.orderStatus?.toLowerCase();
    const actions = [];

    if (status === "in process") {
      actions.push({
        label: "Confirm Order",
        action: "Confirm",
        newStatus: "Confirmed",
        className: "btn btn-primary btn-sm me-2",
      });
      actions.push({
        label: "Cancel Order",
        action: "Cancel",
        newStatus: "Cancelled",
        className: "btn btn-danger btn-sm",
      });
    } else if (status === "confirmed") {
      actions.push({
        label: "Dispatch Order",
        action: "Dispatch",
        newStatus: "Dispatched",
        className: "btn btn-info btn-sm me-2",
      });
      actions.push({
        label: "Cancel Order",
        action: "Cancel",
        newStatus: "Cancelled",
        className: "btn btn-danger btn-sm",
      });
    } else if (status === "dispatched") {
      actions.push({
        label: "Mark as Delivered",
        action: "Deliver",
        newStatus: "Delivered",
        className: "btn btn-success btn-sm",
      });
    }

    // Add payment action for delivered orders
    if (
      status === "delivered" &&
      order.paymentStatus?.toLowerCase() !== "paid"
    ) {
      actions.push({
        label: "Mark as Paid",
        action: "MarkPaid",
        newStatus: "Paid",
        className: "btn btn-success btn-sm ms-2",
        isPayment: true,
      });
    }

    return actions;
  };

  const isStepActive = (stepName) => {
    const status = order.orderStatus?.toLowerCase();

    const steps = ["initiated", "confirmed", "dispatched", "delivered"];
    const currentIndex = steps.indexOf(status);
    const stepIndex = steps.indexOf(stepName);

    return stepIndex <= currentIndex;
  };

  if (loading) {
    return <Loading />;
  }

  if (!pageAccessDetails.viewAccess) {
    return null;
  }

  if (!order) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center">
              <p>Order not found</p>
              <button className="btn btn-primary" onClick={() => navigate(-1)}>
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .progress-step-arrow {
            display: flex;
            background: #f8f9fa;
            border-radius: 0.375rem;
            overflow: hidden;
          }
          .progress-step-arrow .progress-bar {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
            font-weight: 500;
            border-right: 1px solid #dee2e6;
          }
          .progress-step-arrow .progress-bar:last-child {
            border-right: none;
          }
          .progress-step-arrow .progress-bar.initiated {
            background-color: #0d6efd;
            color: white;
          }
          .progress-step-arrow .progress-bar.confirmed {
            background-color: ${
              order.orderStatus === "Confirmed" ||
              order.orderStatus === "Dispatched" ||
              order.orderStatus === "Delivered"
                ? "#0d6efd"
                : "#e9ecef"
            };
            color: ${
              order.orderStatus === "Confirmed" ||
              order.orderStatus === "Dispatched" ||
              order.orderStatus === "Delivered"
                ? "white"
                : "#6c757d"
            };
          }
          .progress-step-arrow .progress-bar.pendingProcess:nth-child(3) {
            background-color: ${
              order.orderStatus === "Dispatched" ||
              order.orderStatus === "Delivered"
                ? "#0d6efd"
                : "#e9ecef"
            };
            color: ${
              order.orderStatus === "Dispatched" ||
              order.orderStatus === "Delivered"
                ? "white"
                : "#6c757d"
            };
          }
          .progress-step-arrow .progress-bar.pendingProcess:nth-child(4) {
            background-color: ${
              order.orderStatus === "Delivered" ? "#0d6efd" : "#e9ecef"
            };
            color: ${order.orderStatus === "Delivered" ? "white" : "#6c757d"};
          }
          .table>:not(caption)>*>* {
            padding: .75rem 0.5rem !important;
          }
        `}
      </style>

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header align-items-center d-flex justify-content-between">
              <h4 className="card-title mb-0 flex-grow-1">
                Order Details - {order.orderId}
              </h4>
              <div className="d-flex align-items-center gap-2">
                <span
                  className={`badge ${getStatusBadgeClass(
                    order.orderStatus
                  )} badge-border`}
                >
                  {order.orderStatus}
                </span>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigate(-1)}
                >
                  <i className="mdi mdi-arrow-left"></i> Back
                </button>
              </div>
            </div>

            <div className="card-body">
              {/* Progress Bar */}
              <div className="row gy-4">
                <div className="col-lg-12">
                  <div className="live-preview">
                    <div className="progress progress-step-arrow progress-info">
                      <div
                        className={`progress-bar initiated ${
                          isStepActive("initiated") ? "active initiated" : ""
                        }`}
                        role="progressbar"
                        style={{ width: "100%" }}
                      >
                        Initiated
                      </div>
                      <div
                        className={`progress-bar  ${
                          isStepActive("confirmed")
                            ? "active confirmed"
                            : "bg-light text-dark "
                        }`}
                        role="progressbar"
                        style={{ width: "100%" }}
                      >
                        Confirmed
                      </div>
                      <div
                        className={`progress-bar pendingProcess ${
                          isStepActive("dispatched")
                            ? "active dispatched"
                            : "bg-light text-dark"
                        }`}
                        role="progressbar"
                        style={{ width: "100%" }}
                      >
                        Dispatched
                      </div>
                      <div
                        className={`progress-bar pendingProcess ${
                          isStepActive("delivered")
                            ? "active delivered"
                            : "bg-light text-dark"
                        }`}
                        role="progressbar"
                        style={{ width: "100%" }}
                      >
                        Delivered
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="col-lg-12">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      <div className="col-lg-3 col-6">
                        <p className="text-muted mb-2 text-uppercase fw-semibold">
                          Order No
                        </p>
                        <h5 className="fs-14 mb-0">#{order.orderId}</h5>
                      </div>
                      <div className="col-lg-3 col-6">
                        <p className="text-muted mb-2 text-uppercase fw-semibold">
                          Date
                        </p>
                        <h5 className="fs-14 mb-0">
                          {formatDate(order.orderedOn)}
                        </h5>
                      </div>
                      <div className="col-lg-3 col-6">
                        <p className="text-muted mb-2 text-uppercase fw-semibold">
                          Order Status
                        </p>
                        <span
                          className={`badge ${getStatusBadgeClass(
                            order.orderStatus
                          )} badge-border`}
                        >
                          {order.orderStatus}
                        </span>
                      </div>
                      <div className="col-lg-3 col-6">
                        <p className="text-muted mb-2 text-uppercase fw-semibold">
                          Payment Status
                        </p>
                        <span
                          className={`badge ${getStatusBadgeClass(
                            order.paymentStatus
                          )} badge-border`}
                        >
                          {order.paymentStatus}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer and Address Info */}
                <div className="col-lg-12">
                  <div className="card-body p-4 border-top border-top-dashed">
                    <div className="row g-3">
                      <div className="col-6">
                        <h6 className="text-muted text-uppercase fw-semibold mb-3">
                          Customer & Billing Address
                        </h6>
                        <p className="text-muted mb-2">
                          <strong>Name: </strong>
                          <span>{order.userName}</span>
                        </p>
                        <p className="text-muted mb-2">
                          <strong>Email: </strong>
                          <a
                            className="text-muted"
                            href={`mailto:${order.emailId}`}
                          >
                            {order.emailId}
                          </a>
                        </p>
                        <p className="text-muted mb-2">
                          <strong>Contact No: </strong>
                          <a
                            className="text-muted"
                            href={`tel:${order.mobileNo}`}
                          >
                            {order.mobileNo}
                          </a>
                        </p>

                        {billingAddress && (
                          <>
                            <p className="text-muted mb-2">
                              <strong>Address: </strong>
                              <span>
                                {billingAddress.buildingName},{" "}
                                {billingAddress.street}, {billingAddress.city},{" "}
                                {billingAddress.state}, {billingAddress.country}{" "}
                                - {billingAddress.pinCode}
                              </span>
                            </p>
                            {billingAddress.companyName && (
                              <p className="text-muted mb-2">
                                <strong>Company: </strong>
                                <span>{billingAddress.companyName}</span>
                              </p>
                            )}
                            {billingAddress.gstNo && (
                              <p className="text-muted mb-2">
                                <strong>GSTIN: </strong>
                                <span>{billingAddress.gstNo}</span>
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      <div className="col-6">
                        <h6 className="text-muted text-uppercase fw-semibold mb-3">
                          Order Actions
                        </h6>
                        <div className="d-flex flex-wrap gap-2">
                          {getAvailableActions().map((action, index) => (
                            <button
                              key={index}
                              className={action.className}
                              onClick={() =>
                                action.isPayment
                                  ? handlePaymentStatusUpdate(order.id)
                                  : handleStatusUpdate(
                                      order.id,
                                      action.action,
                                      action.newStatus
                                    )
                              }
                              disabled={updatingOrderId === order.id}
                            >
                              {updatingOrderId === order.id && (
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                ></span>
                              )}
                              {action.label}
                            </button>
                          ))}
                        </div>

                        {shippingAddress && (
                          <>
                            <h6 className="text-muted text-uppercase fw-semibold mb-3 mt-4">
                              Shipping Address
                            </h6>
                            <p className="text-muted mb-2">
                              <span>
                                {shippingAddress.buildingName},{" "}
                                {shippingAddress.street}, {shippingAddress.city}
                                , {shippingAddress.state},{" "}
                                {shippingAddress.country} -{" "}
                                {shippingAddress.pinCode}
                              </span>
                            </p>
                          </>
                        )}

                        {order.remarks && (
                          <>
                            <h6 className="text-muted text-uppercase fw-semibold mb-3 mt-4">
                              Remarks
                            </h6>
                            <p className="text-muted mb-2">{order.remarks}</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Items Table */}
                <div className="col-lg-12">
                  <div className="card-body p-4">
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered text-left table-nowrap align-middle mb-0">
                        <thead>
                          <tr className="table-active">
                            <th scope="col" style={{ width: "50px" }}>
                              #
                            </th>
                            <th scope="col">Item</th>
                            <th scope="col">Product Size</th>
                            <th scope="col">Price</th>
                            <th scope="col">Quantity</th>
                            <th scope="col">Tax Percent</th>
                            <th scope="col">Tax Amount</th>
                            <th scope="col">Sub Total Without Tax</th>
                            <th scope="col">Total Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(orderItems) &&
                            orderItems.map((item, index) => (
                              <tr key={item.id || index}>
                                <td>{index + 1}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div>
                                      <h6 className="mb-0">
                                        {item.productName}
                                      </h6>
                                    </div>
                                  </div>
                                </td>
                                <td>{item.productSize}</td>
                                <td>{formatCurrency(item.discountPrice)}</td>
                                <td>{item.quantity}</td>
                                <td>{item.taxPercent}%</td>
                                <td>
                                  {formatCurrency(
                                    ((item.discountPrice * item.taxPercent) /
                                      (100 + item.taxPercent)) *
                                      item.quantity
                                  )}
                                </td>
                                <td>
                                  {formatCurrency(
                                    item.discountPrice * item.quantity -
                                      ((item.discountPrice * item.taxPercent) /
                                        (100 + item.taxPercent)) *
                                        item.quantity
                                  )}
                                </td>
                                <td>
                                  <strong>
                                    {formatCurrency(
                                      item.discountPrice * item.quantity
                                    )}
                                  </strong>
                                </td>
                              </tr>
                            ))}

                          {/* Summary rows */}
                          <tr>
                            <td colSpan="8" className="text-end">
                              <strong>SubTotal Without Tax</strong>
                            </td>
                            <td>
                              <strong>
                                {formatCurrency(order.subTotalWithoutTax)}
                              </strong>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="8" className="text-end">
                              <strong>Tax</strong>
                            </td>
                            <td>
                              <strong>{formatCurrency(order.tax)}</strong>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="8" className="text-end">
                              <strong>Shipping Charges</strong>
                            </td>
                            <td>
                              <strong>
                                {formatCurrency(order.shippingPrice)}
                              </strong>
                            </td>
                          </tr>
                          {order.discount > 0 && (
                            <tr>
                              <td colSpan="8" className="text-end">
                                <strong>Discount</strong>
                              </td>
                              <td>
                                <strong className="text-success">
                                  -{formatCurrency(order.discount)}
                                </strong>
                              </td>
                            </tr>
                          )}
                          {order.couponDiscount > 0 && (
                            <tr>
                              <td colSpan="8" className="text-end">
                                <strong>
                                  Coupon Discount{" "}
                                  {order.couponCode && `(${order.couponCode})`}
                                </strong>
                              </td>
                              <td>
                                <strong className="text-success">
                                  -{formatCurrency(order.couponDiscount)}
                                </strong>
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td colSpan="8" className="text-end">
                              <strong>Total Price</strong>
                            </td>
                            <td>
                              <strong className="text-primary fs-16">
                                {formatCurrency(order.totalPrice)}
                              </strong>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="8" className="text-end">
                              <strong>Paid Amount</strong>
                            </td>
                            <td>
                              <strong className="text-success">
                                {formatCurrency(order.paidAmount)}
                              </strong>
                            </td>
                          </tr>
                          {order.balanceAmount > 0 && (
                            <tr>
                              <td colSpan="8" className="text-end">
                                <strong>Balance Amount</strong>
                              </td>
                              <td>
                                <strong className="text-danger">
                                  {formatCurrency(order.balanceAmount)}
                                </strong>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Details Section */}
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header align-items-center d-flex justify-content-between">
              <h4 className="card-title mb-0 flex-grow-1">
                Payment Details - {order.orderId}
              </h4>
            </div>
            <div className="card-body">
              <div className="row gy-4">
                <div className="col-lg-12">
                  <div className="card-body p-4">
                    <div className="table-responsive">
                      {orderPaymentItems && orderPaymentItems.length > 0 ? (
                        <table className="table table-striped table-bordered text-left table-nowrap align-middle mb-0">
                          <thead>
                            <tr className="table-active">
                              <th scope="col" style={{ width: "50px" }}>
                                #
                              </th>
                              <th scope="col">Payment Mode</th>
                              <th scope="col">Transaction ID</th>
                              <th scope="col">Razor Payment ID</th>
                              <th scope="col">Razor Order ID</th>
                              <th scope="col">Amount</th>
                              <th scope="col">Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderPaymentItems.map((payment, index) => (
                              <tr key={payment.id || index}>
                                <td>{index + 1}</td>
                                <td>
                                  <span
                                    className={`badge ${getPaymentModeBadgeClass(
                                      payment.paymentMode
                                    )} badge-border`}
                                  >
                                    {payment.paymentMode}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-monospace">
                                    {payment.transactionId || "-"}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-monospace">
                                    {payment.razorPaymentId || "-"}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-monospace">
                                    {payment.razorOrderId || "-"}
                                  </span>
                                </td>
                                <td>
                                  <strong className="text-success">
                                    {formatCurrency(payment.amount)}
                                  </strong>
                                </td>
                                <td>
                                  <span className="text-muted">
                                    {payment.remarks || "-"}
                                  </span>
                                </td>
                              </tr>
                            ))}

                            {/* Payment Summary */}
                            <tr className="table-info">
                              <td colSpan="5" className="text-end">
                                <strong>Total Payments</strong>
                              </td>
                              <td>
                                <strong className="text-success">
                                  {formatCurrency(
                                    orderPaymentItems.reduce(
                                      (sum, payment) =>
                                        sum + (payment.amount || 0),
                                      0
                                    )
                                  )}
                                </strong>
                              </td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      ) : (
                        <div className="text-center py-4">
                          <div className="mb-3">
                            <i className="mdi mdi-credit-card-off-outline display-4 text-muted"></i>
                          </div>
                          <h5 className="text-muted">
                            No Payment Records Found
                          </h5>
                          <p className="text-muted mb-0">
                            No payment transactions have been recorded for this
                            order yet.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetails;
